import { useQuery } from "@tanstack/react-query";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, X } from "lucide-react";

export interface TrackerXRecord {
  id: string;
  name: string;
  category: string;
  valueType: string;
  unitLabel: string | null;
  period: string;
  goalValue: number | null;
  goalId: string | null;
  isActive: boolean;
}

export interface NewTrackerFields {
  name: string;
  valueType: string;
  unitLabel: string;
  period: string;
  goalValue: string;
}

export const EMPTY_NEW_TRACKER: NewTrackerFields = {
  name: "",
  valueType: "number",
  unitLabel: "",
  period: "daily",
  goalValue: "",
};

interface TrackerXMeasurementSectionProps {
  enabled: boolean;
  selectedTrackerIds: string[];
  onToggleTracker: (id: string) => void;
  newTrackers: NewTrackerFields[];
  onNewTrackersChange: (trackers: NewTrackerFields[]) => void;
  currentGoalId?: string;
}

export function useActiveTrackers(enabled: boolean) {
  const { data: trackers = [], isLoading } = useQuery<TrackerXRecord[]>({
    queryKey: ["/api/trackerx"],
    enabled,
  });
  return { trackers: trackers.filter((t) => t.isActive), isLoading };
}

export default function TrackerXMeasurementSection({
  enabled,
  selectedTrackerIds,
  onToggleTracker,
  newTrackers,
  onNewTrackersChange,
  currentGoalId,
}: TrackerXMeasurementSectionProps) {
  const { trackers, isLoading } = useActiveTrackers(enabled);

  if (!enabled) return null;

  const setField = (index: number, key: keyof NewTrackerFields) => (value: string) =>
    onNewTrackersChange(
      newTrackers.map((t, i) => (i === index ? { ...t, [key]: value } : t)),
    );

  const addTracker = () => onNewTrackersChange([...newTrackers, { ...EMPTY_NEW_TRACKER }]);
  const removeTracker = (index: number) =>
    onNewTrackersChange(newTrackers.filter((_, i) => i !== index));

  return (
    <div className="space-y-4 rounded-md border p-4" data-testid="section-trackerx-measurement">
      <div>
        <Label>TrackerX Records</Label>
        <p className="text-xs text-muted-foreground mt-1">
          Link one or more active TrackerX records (e.g., protein, fat, carbs) to measure this goal.
          Each tracker is judged by its own period, and the goal's progress is the average across them.
        </p>
      </div>

      {isLoading && (
        <p className="text-sm text-muted-foreground">Loading trackers...</p>
      )}

      {trackers.length > 0 && (
        <div className="space-y-2" data-testid="list-trackerx-records">
          {trackers.map((t) => (
            <label
              key={t.id}
              className="flex items-start gap-2 cursor-pointer text-sm"
              data-testid={`option-trackerx-${t.id}`}
            >
              <Checkbox
                checked={selectedTrackerIds.includes(t.id)}
                onCheckedChange={() => onToggleTracker(t.id)}
                data-testid={`checkbox-trackerx-${t.id}`}
              />
              <span>
                {t.name}
                {t.goalValue != null && t.goalValue > 0
                  ? ` — goal ${t.goalValue}${t.unitLabel ? ` ${t.unitLabel}` : ""} (${t.period})`
                  : ` (${t.period})`}
                {t.goalValue == null || t.goalValue <= 0 ? (
                  <span className="text-muted-foreground"> — no target set, won't count toward progress</span>
                ) : null}
                {t.goalId && t.goalId !== currentGoalId ? (
                  <span className="text-muted-foreground"> — linked to another goal</span>
                ) : null}
              </span>
            </label>
          ))}
        </div>
      )}

      {trackers.length === 0 && !isLoading && (
        <p className="text-sm text-muted-foreground" data-testid="text-no-active-trackers">
          No active TrackerX records found. Create a new one below.
        </p>
      )}

      {newTrackers.map((newTracker, index) => (
        <div
          key={index}
          className="space-y-3 rounded-md border p-3"
          data-testid={`section-new-tracker-form-${index}`}
        >
          <div className="flex items-center justify-between">
            <Label>New TrackerX record {newTrackers.length > 1 ? `#${index + 1}` : ""}</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-muted-foreground"
              onClick={() => removeTracker(index)}
              data-testid={`button-remove-new-tracker-${index}`}
            >
              <X className="w-4 h-4 mr-1" />
              Remove
            </Button>
          </div>
          <div className="space-y-1">
            <Label>Tracker Name *</Label>
            <Input
              value={newTracker.name}
              onChange={(e) => setField(index, "name")(e.target.value)}
              placeholder="e.g. Calories, Carbs, Steps"
              data-testid={`input-new-tracker-name-${index}`}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Value Type</Label>
              <Select value={newTracker.valueType} onValueChange={setField(index, "valueType")}>
                <SelectTrigger data-testid={`select-new-tracker-value-type-${index}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="weight">Weight</SelectItem>
                  <SelectItem value="currency">Currency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Unit Label</Label>
              <Input
                value={newTracker.unitLabel}
                onChange={(e) => setField(index, "unitLabel")(e.target.value)}
                placeholder="e.g. cal, g, lbs"
                data-testid={`input-new-tracker-unit-${index}`}
              />
            </div>
            <div className="space-y-1">
              <Label>Period</Label>
              <Select value={newTracker.period} onValueChange={setField(index, "period")}>
                <SelectTrigger data-testid={`select-new-tracker-period-${index}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Target Value</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={newTracker.goalValue}
                onChange={(e) => setField(index, "goalValue")(e.target.value)}
                placeholder="e.g. 1200"
                data-testid={`input-new-tracker-goal-value-${index}`}
              />
            </div>
          </div>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addTracker}
        data-testid="button-add-new-tracker"
      >
        <Plus className="w-4 h-4 mr-1" />
        {newTrackers.length === 0 ? "Create a new TrackerX record" : "Add another TrackerX record"}
      </Button>

      {newTrackers.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {newTrackers.length === 1
            ? "The new tracker will be created and linked to this goal automatically."
            : "Each new tracker will be created and linked to this goal automatically, and the goal's progress is the average across all linked trackers."}
        </p>
      )}
    </div>
  );
}
